import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Config from '../utils/Config';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCircleCheck, faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import {
  displayMessage,
} from '../utils/functions';
import "./Login.css";
import "./ForgotPassword.css";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [forgotPasswordComplete, setForgotPassword] = useState(false); // ✅ State to track successful password reset sent
  const [messageClass, setMessageClass] = useState("warning hidden");
  const [isResend, setIsResend] = useState(false); // ✅ NEW STATE
  const [lastSentTime, setLastSentTime] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0); // in seconds

  const navigate = useNavigate();
  const emailInputRef = useRef(null);
  const messageRef = useRef(null);

  useEffect(() => {
    const focusEmail = () => {
      emailInputRef.current?.focus();
    };

    // Focus on mount
    focusEmail();

    // Refocus on any document click
    document.addEventListener("click", focusEmail);

    return () => {
      document.removeEventListener("click", focusEmail);
    };
  }, []);

  const validateForm = () => {
    if (!email.trim()) {
      displayMessage(messageRef, "❌ Email is required.", "warning", setMessageClass);
      return false;
    }
    return true;
  };

  const handleLostPassword = async (e) => {
    e.preventDefault();

    const now = Date.now();
    const cooldownPeriod = 60 * 1000; // 60 seconds

    if (lastSentTime && now - lastSentTime < cooldownPeriod) {
      const secondsLeft = Math.ceil((cooldownPeriod - (now - lastSentTime)) / 1000);
      displayMessage(messageRef, `⏱️ Please wait ${secondsLeft}s before trying again.`, "warning", setMessageClass);
      return;
    }

    if (!validateForm()) return;

    // Hide message first to trigger reflow
    if (messageRef.current) {
      messageRef.current.style.display = "none";
      setMessageClass("hidden");
    }

    try {
      const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_PASSWORD_LINK}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setLastSentTime(now);
        setResendCooldown(60); // set cooldown in seconds
        setForgotPassword(true); // on first request only

        setTimeout(() => {
          displayMessage(messageRef, "✅ A new password reset link was sent.", "success", setMessageClass);
        }, 200); // short delay to force visual feedback
      } else {
        const errorMsg = data.error || data.message || "Something went wrong. Try again.";
        displayMessage(messageRef, `❌ ${errorMsg}`, "warning", setMessageClass);
      }
    } catch (error) {

    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  return (
    <div className="login-container">
      <div className="login-box" id="forgot-password">
        {/* ✅ Show success message after password reset sent */}
        {forgotPasswordComplete ? (
          <div className="passwordRequestInfo">
            <h2><FontAwesomeIcon icon={faCircleCheck} style={{ color: "#28a745" }} />&nbsp;&nbsp;Password Reset Sent</h2>
            <div ref={messageRef} className={messageClass} id="message" />
            <p>
              Check your inbox and click the link in the email to reset your password.
              If you don’t see it, check your spam or junk folder.
            </p>
            <button className="login-button" onClick={() => navigate("/")}>
              Home
            </button>
            <p>
              <button
                className="resend-button"
                onClick={(e) => handleLostPassword(e, true)}
                disabled={resendCooldown > 0}
              >
                <FontAwesomeIcon icon={faArrowRotateRight} />
                &nbsp;Resend Password Reset {resendCooldown > 0 ? `(${resendCooldown}s)` : ""}
              </button>
            </p>
          </div>
        ) : (
          // ✅ Show password reset form
          <>
            <h2>
              <span className="titleIcon"><FontAwesomeIcon icon={faKey} /></span>&nbsp;Forgot Password
            </h2>
            <div ref={messageRef} className={messageClass} id="message"></div> {/* ✅ Message Ref */}
            <p className="forgot-password-text-intro">
              Please enter your email address that you used to register with and we will email you a link to reset your password.
            </p>
            <form onSubmit={handleLostPassword}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                ref={emailInputRef}
                title="Enter your email"
              />
              <button
                type="submit"
                id="register-button"
                className="login-button"
                disabled={resendCooldown > 0}
              >
                Continue {resendCooldown > 0 ? `(${resendCooldown}s)` : ""}
              </button>
            </form>

            <div className="register-box">
              <p>Remembered your password!</p>
              <button className="register-button" id="login-button" onClick={() => navigate("/login")}>
                Log In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
